import { useEffect, useReducer, useState } from 'react';
import './ConfigOverlay.css';

// Define the state structure
interface ConfigState {
  enableNoiseReduction: boolean;
  type: 'VOICE' | 'VIDEO';
  layout: 'TILE' | 'SIDEBAR' | 'SPOTLIGHT';
  autoStartRecording: boolean;
  autoStartTranscription: boolean;
  hideRecordingButton: boolean;
  hideControlPanel: boolean;
  hideLeaveSessionButton: boolean;
  hideHeaderPanel: boolean;
  hideRaiseHandButton: boolean;
  hideShareInviteButton: boolean;
  hideChangeLayoutButton: boolean;
  hideToggleAudioButton: boolean;
  hideToggleVideoButton: boolean;
  hideParticipantListButton: boolean;
  hideChatButton: boolean;
  hideScreenSharingButton: boolean;
  hideClosedCaptionButton: boolean;
  hideTranscriptionButton: boolean;
  hideSessionTimer: boolean;
  hideNetworkIndicator: boolean;
  enableParticipantContextMenu: boolean;
  // lowBandwidthMode: boolean;
  displayName: string;
  idleTimeoutPeriodBeforePrompt: number;
  idleTimeoutPeriodAfterPrompt: number;
}

// Define action types
type ConfigAction =
  | { type: 'TOGGLE_BOOLEAN'; field: keyof ConfigState }
  | { type: 'SET_SELECT'; field: keyof ConfigState; value: string }
  | { type: 'SET_NUMBER'; field: keyof ConfigState; value: number }
  | { type: 'SET_TEXT'; field: keyof ConfigState; value: string };

// Initial state
const initialState: ConfigState = {
  enableNoiseReduction: false,
  type: 'VIDEO',
  layout: 'TILE',
  autoStartRecording: false,
  autoStartTranscription: false,
  hideRecordingButton: true,
  hideControlPanel: false,
  hideLeaveSessionButton: false,
  hideHeaderPanel: false,
  hideRaiseHandButton: false,
  hideShareInviteButton: false,
  hideChangeLayoutButton: false,
  hideToggleAudioButton: false,
  hideToggleVideoButton: false,
  hideParticipantListButton: false,
  hideChatButton: false,
  hideScreenSharingButton: false,
  hideClosedCaptionButton: true,
  hideTranscriptionButton: true,
  hideSessionTimer: false,
  hideNetworkIndicator: true,
  enableParticipantContextMenu: true,
  // lowBandwidthMode: false,
  displayName: '',
  idleTimeoutPeriodBeforePrompt: 60000,
  idleTimeoutPeriodAfterPrompt: 180000,
};

// Reducer function
const configReducer = (
  state: ConfigState,
  action: ConfigAction,
): ConfigState => {
  switch (action.type) {
    case 'TOGGLE_BOOLEAN':
      return {
        ...state,
        [action.field]: !state[action.field],
      };
    case 'SET_SELECT':
    case 'SET_TEXT':
      return {
        ...state,
        [action.field]: action.value,
      };
    case 'SET_NUMBER':
      return {
        ...state,
        [action.field]: action.value,
      };
    default:
      return state;
  }
};

const ConfigOverlay = () => {
  const [state, dispatch] = useReducer(configReducer, initialState);
  const [showConfigOverlay, setShowConfigOverlay] = useState(false);

  // Log state changes
  useEffect(() => {
    console.log('Config state changed:', state);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (window as any).configState = state; // TODO: Remove this
  }, [state]);

  useEffect(() => {
    // detect when ? is pressed on keyboard
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === '?') {
        setShowConfigOverlay(prev => !prev);
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  const handleCheckboxChange = (field: keyof ConfigState) => {
    dispatch({ type: 'TOGGLE_BOOLEAN', field });
  };

  const handleSelectChange = (field: keyof ConfigState, value: string) => {
    dispatch({ type: 'SET_SELECT', field, value });
  };

  const handleNumberChange = (field: keyof ConfigState, value: number) => {
    dispatch({ type: 'SET_NUMBER', field, value });
  };

  const handleTextChange = (field: keyof ConfigState, value: string) => {
    dispatch({ type: 'SET_TEXT', field, value });
  };

  if (!showConfigOverlay) return null;

  return (
    <div className="config-overlay">
      <div className="config-overlay-card">
        <div className="config-overlay-header">
          <h4>Call Configuration</h4>
        </div>
        <div className="config-overlay-grid">
          <div className="config-row">
            <label>Enable noise reduction</label>
            <input
              name="enableNoiseReduction"
              type="checkbox"
              checked={state.enableNoiseReduction}
              onChange={() => handleCheckboxChange('enableNoiseReduction')}
            />
          </div>

          <div className="config-field">
            <label>Session type</label>
            <select
              name="type"
              value={state.type}
              onChange={e => handleSelectChange('type', e.target.value)}>
              <option value="VIDEO">Video</option>
              <option value="VOICE">Voice</option>
            </select>
          </div>

          <div className="config-field">
            <label>Layout</label>
            <select
              name="layout"
              value={state.layout}
              onChange={e => handleSelectChange('layout', e.target.value)}>
              <option value="TILE">Tile</option>
              <option value="SIDEBAR">Sidebar</option>
              <option value="SPOTLIGHT">Spotlight</option>
            </select>
          </div>

          <div className="config-row">
            <label>Auto start recording</label>
            <input
              name="autoStartRecording"
              type="checkbox"
              checked={state.autoStartRecording}
              onChange={() => handleCheckboxChange('autoStartRecording')}
            />
          </div>

          <div className="config-row">
            <label>Auto start transcription</label>
            <input
              name="autoStartTranscription"
              type="checkbox"
              checked={state.autoStartTranscription}
              onChange={() => handleCheckboxChange('autoStartTranscription')}
            />
          </div>

          <div className="config-row">
            <label>Hide recording button</label>
            <input
              name="hideRecordingButton"
              type="checkbox"
              checked={state.hideRecordingButton}
              onChange={() => handleCheckboxChange('hideRecordingButton')}
            />
          </div>

          <div className="config-row">
            <label>Hide control panel</label>
            <input
              name="hideControlPanel"
              type="checkbox"
              checked={state.hideControlPanel}
              onChange={() => handleCheckboxChange('hideControlPanel')}
            />
          </div>

          <div className="config-row">
            <label>Hide leave session button</label>
            <input
              name="hideLeaveSessionButton"
              type="checkbox"
              checked={state.hideLeaveSessionButton}
              onChange={() => handleCheckboxChange('hideLeaveSessionButton')}
            />
          </div>

          <div className="config-row">
            <label>Hide header panel</label>
            <input
              name="hideHeaderPanel"
              type="checkbox"
              checked={state.hideHeaderPanel}
              onChange={() => handleCheckboxChange('hideHeaderPanel')}
            />
          </div>

          <div className="config-row">
            <label>Hide raise hand button</label>
            <input
              name="hideRaiseHandButton"
              type="checkbox"
              checked={state.hideRaiseHandButton}
              onChange={() => handleCheckboxChange('hideRaiseHandButton')}
            />
          </div>

          <div className="config-row">
            <label>Hide share invite button</label>
            <input
              name="hideShareInviteButton"
              type="checkbox"
              checked={state.hideShareInviteButton}
              onChange={() => handleCheckboxChange('hideShareInviteButton')}
            />
          </div>

          <div className="config-row">
            <label>Hide change layout button</label>
            <input
              name="hideChangeLayoutButton"
              type="checkbox"
              checked={state.hideChangeLayoutButton}
              onChange={() => handleCheckboxChange('hideChangeLayoutButton')}
            />
          </div>

          <div className="config-row">
            <label>Hide toggle audio button</label>
            <input
              name="hideToggleAudioButton"
              type="checkbox"
              checked={state.hideToggleAudioButton}
              onChange={() => handleCheckboxChange('hideToggleAudioButton')}
            />
          </div>

          <div className="config-row">
            <label>Hide toggle video button</label>
            <input
              name="hideToggleVideoButton"
              type="checkbox"
              checked={state.hideToggleVideoButton}
              onChange={() => handleCheckboxChange('hideToggleVideoButton')}
            />
          </div>

          <div className="config-row">
            <label>Hide participant list button</label>
            <input
              name="hideParticipantListButton"
              type="checkbox"
              checked={state.hideParticipantListButton}
              onChange={() => handleCheckboxChange('hideParticipantListButton')}
            />
          </div>

          <div className="config-row">
            <label>Hide chat button</label>
            <input
              name="hideChatButton"
              type="checkbox"
              checked={state.hideChatButton}
              onChange={() => handleCheckboxChange('hideChatButton')}
            />
          </div>

          <div className="config-row">
            <label>Hide screen sharing button</label>
            <input
              name="hideScreenSharingButton"
              type="checkbox"
              checked={state.hideScreenSharingButton}
              onChange={() => handleCheckboxChange('hideScreenSharingButton')}
            />
          </div>

          <div className="config-row">
            <label>Hide closed caption button</label>
            <input
              name="hideClosedCaptionButton"
              type="checkbox"
              checked={state.hideClosedCaptionButton}
              onChange={() => handleCheckboxChange('hideClosedCaptionButton')}
            />
          </div>

          <div className="config-row">
            <label>Hide transcription button</label>
            <input
              name="hideTranscriptionButton"
              type="checkbox"
              checked={state.hideTranscriptionButton}
              onChange={() => handleCheckboxChange('hideTranscriptionButton')}
            />
          </div>

          <div className="config-row">
            <label>Hide session timer</label>
            <input
              name="hideSessionTimer"
              type="checkbox"
              checked={state.hideSessionTimer}
              onChange={() => handleCheckboxChange('hideSessionTimer')}
            />
          </div>

          <div className="config-row">
            <label>Hide network indicator</label>
            <input
              name="hideNetworkIndicator"
              type="checkbox"
              checked={state.hideNetworkIndicator}
              onChange={() => handleCheckboxChange('hideNetworkIndicator')}
            />
          </div>

          <div className="config-row">
            <label>Enable participant context menu</label>
            <input
              name="enableParticipantContextMenu"
              type="checkbox"
              checked={state.enableParticipantContextMenu}
              onChange={() =>
                handleCheckboxChange('enableParticipantContextMenu')
              }
            />
          </div>

          {/* <div className="config-row">
            <label>Low bandwidth mode</label>
            <input
              name="lowBandwidthMode"
              type="checkbox"
              checked={state.lowBandwidthMode}
              onChange={() => handleCheckboxChange('lowBandwidthMode')}
            />
          </div> */}

          <div className="config-field">
            <label>Display name</label>
            <input
              name="displayName"
              type="text"
              placeholder="Enter display name"
              value={state.displayName}
              onChange={e => handleTextChange('displayName', e.target.value)}
            />
          </div>

          <div className="config-field">
            <label>Idle timeout before prompt (ms)</label>
            <input
              name="idleTimeoutPeriodBeforePrompt"
              type="number"
              value={state.idleTimeoutPeriodBeforePrompt}
              onChange={e =>
                handleNumberChange(
                  'idleTimeoutPeriodBeforePrompt',
                  parseInt(e.target.value) || 0,
                )
              }
            />
          </div>

          <div className="config-field">
            <label>Idle timeout after prompt (ms)</label>
            <input
              name="idleTimeoutPeriodAfterPrompt"
              type="number"
              value={state.idleTimeoutPeriodAfterPrompt}
              onChange={e =>
                handleNumberChange(
                  'idleTimeoutPeriodAfterPrompt',
                  parseInt(e.target.value) || 0,
                )
              }
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConfigOverlay;
